import * as CONSTANTS from "../utils/constants/constants";
import {
  AppointmentListResponse,
  AvailabilityResponse,
  CareTeamAppointment,
  TimeOffResponse,
  UserTimeOffBlock,
  UserWorkingHour,
  WorkingHoursResponse,
} from "../interfaces/Scheduling";

const BASE_URL = `${CONSTANTS.HOST}api/v1/scheduling`;

const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

const buildUrl = (path: string, params?: Record<string, any>) => {
  const url = new URL(`${BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Scheduling API request failed");
  }
  return response.json() as Promise<T>;
};

export default class SchedulingService {
  // Working Hours
  static async listWorkingHours(token: string, userId?: number) {
    const url = buildUrl("/working-hours/", userId ? { user_id: userId } : undefined);
    const response = await fetch(url, {
      method: "GET",
      headers: authHeaders(token),
    });
    return handleResponse<WorkingHoursResponse>(response);
  }

  static async createWorkingHour(token: string, payload: Partial<UserWorkingHour> & { weekday: number }) {
    const url = buildUrl("/working-hours/");
    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    });
    return handleResponse<UserWorkingHour>(response);
  }

  static async updateWorkingHour(token: string, id: number, payload: Partial<UserWorkingHour>) {
    const url = buildUrl(`/working-hours/${id}/`);
    const response = await fetch(url, {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    });
    return handleResponse<UserWorkingHour>(response);
  }

  static async deleteWorkingHour(token: string, id: number) {
    const url = buildUrl(`/working-hours/${id}/`);
    const response = await fetch(url, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Failed to delete working hour");
    }
  }

  // Time off blocks
  static async listTimeOff(token: string, userId?: number) {
    const url = buildUrl("/time-off/", userId ? { user_id: userId } : undefined);
    const response = await fetch(url, {
      method: "GET",
      headers: authHeaders(token),
    });
    return handleResponse<TimeOffResponse>(response);
  }

  static async createTimeOff(token: string, payload: Omit<UserTimeOffBlock, "id"> & { start_time: string; end_time: string }) {
    const url = buildUrl("/time-off/");
    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    });
    return handleResponse<UserTimeOffBlock>(response);
  }

  static async updateTimeOff(token: string, id: number, payload: Partial<UserTimeOffBlock>) {
    const url = buildUrl(`/time-off/${id}/`);
    const response = await fetch(url, {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    });
    return handleResponse<UserTimeOffBlock>(response);
  }

  static async deleteTimeOff(token: string, id: number) {
    const url = buildUrl(`/time-off/${id}/`);
    const response = await fetch(url, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Failed to delete time off block");
    }
  }

  // Appointments
  static async listAppointments(
    token: string,
    params?: {
      start?: string;
      end?: string;
      status?: string;
      patient_id?: string;
      user_id?: number;
    }
  ) {
    const url = buildUrl("/appointments/", params);
    const response = await fetch(url, {
      method: "GET",
      headers: authHeaders(token),
    });
    return handleResponse<AppointmentListResponse>(response);
  }

  static async createAppointment(
    token: string,
    payload: Pick<CareTeamAppointment, "patient_id" | "start_time" | "end_time" | "notes"> & {
      user_id?: number;
      cohort_id?: number;
      clinic_id?: number;
    }
  ) {
    const url = buildUrl("/appointments/");
    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    });
    return handleResponse<CareTeamAppointment>(response);
  }

  static async updateAppointment(
    token: string,
    id: string,
    payload: Partial<CareTeamAppointment>
  ) {
    const url = buildUrl(`/appointments/${id}/`);
    const response = await fetch(url, {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    });
    return handleResponse<CareTeamAppointment>(response);
  }

  static async cancelAppointment(token: string, id: string) {
    const url = buildUrl(`/appointments/${id}/`);
    const response = await fetch(url, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Failed to cancel appointment");
    }
  }

  static async checkAvailability(
    token: string,
    params: {
      patient_id: string;
      start: string;
      end: string;
      slot_minutes?: number;
      max_slots?: number;
      user_id?: number;
    }
  ) {
    const url = buildUrl("/availability/", params);
    const response = await fetch(url, {
      method: "GET",
      headers: authHeaders(token),
    });
    return handleResponse<AvailabilityResponse>(response);
  }
}
